export type eventType =
  | "started"
  | "message_delta"
  | "done"
  | "error"
  | "chat_renamed";

export type JsonPrimitive = string | number | boolean | null;

export interface JsonObject {
  [key: string]: JsonValue;
}

export interface JsonArray extends Array<JsonValue> {}

export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

export interface SseParsedEvent {
  event: eventType;
  data?: JsonValue;
}

const SSE_EVENT_FIELD = "event:";
const SSE_DATA_FIELD = "data:";
const SSE_COMMENT_PREFIX = ":";

const CRLF_REGEX = /\r\n/g;
const LF = "\n";

const parseSseData = (input: string): JsonValue => {
  const trimmed = input.trim();
  try {
    return JSON.parse(trimmed) as JsonValue;
  } catch {
    // If backend ever emits non-JSON data, treat it as a raw string payload.
    return trimmed;
  }
};

/**
 * Parses a *single*, fully-buffered SSE event payload (no streaming / buffering here).
 * Example input:
 *   "event: message\ndata: {\"text\":\"hi\"}"
 */
export const parseSseEvent = (rawEvent: string): SseParsedEvent | null => {
  const normalized = rawEvent.replace(CRLF_REGEX, LF);
  const trimmed = normalized.trim();

  // Empty keepalive chunks (or extra delimiters) should be ignored by the streamer.
  if (!trimmed) {
    return null;
  }

  const lines = trimmed.split(LF);

  let eventName: eventType | undefined;
  const dataLines: string[] = [];

  lines.forEach((line) => {
    const l = line.trimEnd();

    // Comment/keepalive line in SSE format
    if (!l || l.startsWith(SSE_COMMENT_PREFIX)) {
      return;
    }

    if (l.startsWith(SSE_EVENT_FIELD)) {
      eventName = l.slice(SSE_EVENT_FIELD.length).trim() as eventType;
      return;
    }

    if (l.startsWith(SSE_DATA_FIELD)) {
      dataLines.push(l.slice(SSE_DATA_FIELD.length).trimStart());
      return;
    }
  });

  const dataString = dataLines.join(LF);

  // Backend should always set event type; if it's missing, ignore this chunk.
  if (!eventName) {
    return null;
  }

  // Allow "event-only" messages with no data (SSE spec allows it).
  if (!dataString) {
    return { event: eventName };
  }

  return {
    event: eventName,
    data: parseSseData(dataString),
  };
};
